import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {Label, RelativeTime} from '@primer/react'
import {memo} from 'react'

import {ProjectRowActionMenu} from './ProjectRowActionMenu'
import styles from './RepositoryProjects.module.css'
import type {Project} from './types'

interface ProjectRowProps {
  project: Project
  /**
   * Fired when the viewer selects "Remove project" from the row kebab. Provided
   * by `ProjectList`, which owns the mutation and its error toast.
   */
  onUnlinkProject: (project: Project) => void
}

function ProjectRowComponent({project, onUnlinkProject}: ProjectRowProps) {
  const title = (
    <ListItemTitle value={project.title} href={project.url}>
      {!project.public && (
        <Label size="small" variant="secondary" className={styles.privateLabel}>
          Private
        </Label>
      )}
    </ListItemTitle>
  )

  return (
    <ListItem
      title={title}
      role="listitem"
      className={styles.projectRow}
      secondaryActions={
        // Mirrors the Rails view: the row kebab is hidden entirely when the viewer
        // has no actionable items. Today that's a single "Remove project" gated on
        // per-row write access; copy items are deferred (github/issues#21359).
        project.viewerCanWrite ? (
          <ProjectRowActionMenu project={project} onUnlinkProject={onUnlinkProject} />
        ) : undefined
      }
    >
      <ListItemMainContent>
        <ListItemDescription className={styles.listItemContent}>
          {project.shortDescription && <p className={styles.projectDescription}>{project.shortDescription}</p>}
          <div className={styles.projectMeta}>
            <span>
              #{project.number}
              {project.updatedAt && (
                <>
                  {' '}
                  updated <RelativeTime date={new Date(project.updatedAt)} />
                </>
              )}
            </span>
          </div>
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

export const ProjectRow = memo(ProjectRowComponent)
